import Head from "next/head";
import Link from "next/link";

import { constants } from "src/shared/constants";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import { routes } from "src/shared/routes";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "What is the Suns Up Ticket Pass?",
    answer:
      "The Suns Up Ticket Pass is a flexible monthly ticket plan that gives you access to up to eight (8) Phoenix Suns home games each month for one low price - $99 per month for upper-level seating, or $499 per month for lower-level seating.",
    group: "OVERVIEW",
  },
  {
    question: "How does it work?",
    answer: (
      <>
        Once you purchase your Suns Up Ticket Pass, you&apos;ll receive access
        to claim tickets for the games you are able to attend.
        <br />
        Tickets for any game within the Suns Up Ticket Pass will open the
        morning of an eligible game at 10AM and will be available until three
        (3) hours prior to tip-off (while supplies last).
      </>
    ),
    group: "OVERVIEW",
  },
  {
    question: "What games are included in the Suns Up Ticket Pass?",
    answer:
      "Most Suns games are included. The number of games varies based on availability by month with up to 8 games each month (October-March). Each month's available games will be posted on the Suns website and communicated to all passholders prior to the next month.",
    group: "OVERVIEW",
  },
  {
    question: "Are seats guaranteed for every available game?",
    answer:
      "Seats are on a first-come, first-served basis for eligible games. We recommend claiming your seats for each game as soon as the offer opens 24 hours prior to a game.",
    group: "SEAT/TICKET INFORMATION",
  },
  {
    question: "Where are my seats each game?",
    answer:
      "Seats will be randomly assigned and vary by game depending on availability within your pass plan.",
    group: "SEAT/TICKET INFORMATION",
  },
  {
    question: "How many passes can I buy?",
    answer: "You can buy up to four (4) passes per account.",
    group: "SEAT/TICKET INFORMATION",
  },
  {
    question:
      "If I buy two or more passes, will my seats be next to each other?",
    answer:
      "Yes, passes purchased together will receive their seats next to each other as long as they are the same type of pass.",
    group: "SEAT/TICKET INFORMATION",
  },
  {
    question:
      "Can my seat be next to a friend who also purchased the Suns Up Ticket Pass?",
    answer:
      "Yes, if your friend purchased the same type of Suns Up Ticket Pass as you, you can link your pass to another pass buyer to sit next to each other. You can do this by logging into your Suns Up Ticket Pass Account, clicking “My Group” in the menu, and generating a unique link to share with friends to sit with you this season.",
    group: "SEAT/TICKET INFORMATION",
  },
  {
    question: "How do I get my tickets?",
    answer: (
      <>
        Once you claim tickets for an available game, your tickets will appear
        in your account three (3) hours prior to tip-off. You can check your
        Suns{" "}
        <Link target="_blank" href="https://am.ticketmaster.com/suns">
          Account Manager
        </Link>{" "}
        or in the{" "}
        <Link target="_blank" href="https://www.nba.com/suns/app">
          Suns app
        </Link>{" "}
        to view your tickets.
      </>
    ),
    group: "SEAT/TICKET INFORMATION",
  },
  {
    question:
      "Can I buy additional tickets outside of what is included in my pass?",
    answer:
      "This functionality may be available for select dates throughout the season. Exclusive offers for passholders will be communicated via text.",
    group: "SEAT/TICKET INFORMATION",
  },
  {
    question:
      "If I purchased my Suns Up Ticket Pass in the Upper-Level but wanted to upgrade my seats for a game, am I able to do so?",
    answer:
      "Yes, when you go to claim your ticket(s) for a game, you will have the option to upgrade to lower-level seats for an additional cost.",
    group: "SEAT/TICKET INFORMATION",
  },
  {
    question:
      "For games that Suns Up Ticket Pass isn't available, can I still buy tickets?",
    answer: (
      <>
        If you are interested in purchasing tickets to a game not included in
        the Suns Up Pass, it is recommended to purchase tickets from the Suns{" "}
        <Link href="https://www.nba.com/suns/tickets/single" target="_blank">
          Single Game Ticket
        </Link>{" "}
        page or via{" "}
        <Link
          href="https://www.ticketmaster.com/phoenix-suns-tickets/artist/806004?home_away=home"
          target="_blank"
        >
          Ticketmaster
        </Link>
        .
      </>
    ),
    group: "SEAT/TICKET INFORMATION",
  },
  {
    question: "Can I get a refund for unused tickets?",
    answer: "Passes are non-transferable and non-refundable.",
    group: "SEAT/TICKET INFORMATION",
  },
  {
    question: "Can I sell my pass for games that I do not attend?",
    answer: "No, passes are non-transferable.",
    group: "SEAT/TICKET INFORMATION",
  },
  {
    question:
      "If other members of my party are arriving separately than me, am I able to transfer my tickets to them?",
    answer:
      "Yes, once you receive the tickets in your Account Manager, you can transfer your tickets to other people in your party.",
    group: "SEAT/TICKET INFORMATION",
  },
  {
    question:
      "Is there a long-term commitment or contract for the Suns Up Ticket Pass?",
    answer:
      "No long-term commitment! The Suns Up Ticket Pass renews automatically each month but can be canceled during a 10-day opt-out window prior to being billed.",
    group: "OTHER",
  },
  {
    question: "Can I upgrade my Suns Up Ticket Pass after I purchase?",
    answer:
      "Yes. If you purchase the Basic plan, you can upgrade during your 10-day opt-out window to the Preferred plan the following month.",
    group: "OTHER",
  },
  {
    question:
      "How do I opt out of next month if I don't want to continue with my pass?",
    answer:
      "On the 15th of each month, you will receive a notice that the opt-out period is open. You will have ten (10) days through the 24th of each month to opt-out or alter your plan type. You will be charged on the 25th for the following month if you do not opt out.",
    group: "OTHER",
  },
  {
    question: "Can I share my Suns Up Ticket Pass with someone?",
    answer:
      "Passes themselves cannot be shared and are non-transferable. Tickets claimed through your pass can be transferred.",
    group: "OTHER",
  },
  {
    question: "Are playoff games included in the Suns Up Ticket Pass?",
    answer:
      "No, the Suns Up Ticket Pass is available for regular season games only. As a passholder, you will receive early access to any Suns playoff presale opportunities!",
    group: "OTHER",
  },
  {
    question:
      "Do I receive any other benefits for purchasing the Suns Up Ticket Pass?",
    answer:
      "You will get early access to any Suns playoff presale opportunities!",
    group: "OTHER",
  },
];

export const TERMS_AND_CONDITION: TTeamConfig["LANGUAGES"]["TERMS_AND_CONDITION"] =
  () => (
    <>
      By purchasing you agree to receive SMS alerts regarding ticket delivery
      and agree to the{" "}
      <Link target={"_blank"} href="https://www.attend.tech/terms-of-use">
        Terms of Use
      </Link>{" "}
      and{" "}
      <Link target={"_blank"} href="https://www.attend.tech/privacy-policy">
        Privacy Policy
      </Link>
    </>
  );

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-google-tag-manager`}
        dangerouslySetInnerHTML={{
          __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-KWV2TZZH');
              console.log('======phoenixsuns google tag manager loaded=====');
          `,
        }}
      />

      <noscript id={`${TEAM_SCHEMA}-google-tag-manager-noscript`}>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-KWV2TZZH"
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        ></iframe>
      </noscript>

      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '368362963346231');
            fbq('track', 'PageView');
            window.addEventListener("${constants.customEvents.purchaseSuccess}", function (e) {
              try {
                const { cartRes, checkoutRes, passes } = e.detail;

                let content_name = "";
                if (cartRes.products && cartRes.products.length) {
                  content_name = "${TEAM_SCHEMA}_single_game_purchases";
                };

                if (cartRes.passes && cartRes.passes.length && passes.length) {
                  const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                  content_name = passDetail.passTitle;
                };

                fbq('track', 'InitiateCheckout', {
                  content_name: content_name, 
                  value: String(cartRes.totalAmount), 
                  currency: 'USD', 
                  order_id: checkoutRes.orderNumber,
                });

                console.log('meta pixel purchase event fired.');
              } catch(error) {
                console.log('meta pixel purchase event error:', error); 
              }
            });`,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=368362963346231&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
};
